import { useMemo, useState } from 'react'
import { useParams, useNavigate, Link } from '@tanstack/react-router'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Textarea } from './ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { mdiArrowLeft, mdiContentSave, mdiTrashCanOutline } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { useContractStakeholderType, useUpdateContractStakeholderType, useDeleteContractStakeholderType } from '../hooks/useQueries'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useToast } from '../hooks/use-toast'
import { useTranslation } from 'react-i18next'
import { useCan } from '../contexts/PermissionsContext'

export function ContractStakeholderTypePage() {
  const { org, contractStakeholderTypeUid } = useParams({ strict: false })
  const navigate = useNavigate()
  const { toast } = useToast()
  const { t } = useTranslation('types')
  // Editing a contract stakeholder type is `configuration.write`. Without it
  // the form still renders — the list page links here as read navigation — but
  // read-only, with Save and Delete gone (spec 2026-09-04-02, "Gating").
  const canWrite = useCan('configuration.write')

  const { data: stakeholderType, isLoading, error } = useContractStakeholderType(org || 'demo', contractStakeholderTypeUid || '')
  const updateMutation = useUpdateContractStakeholderType(org || 'demo', contractStakeholderTypeUid || '')
  const deleteMutation = useDeleteContractStakeholderType(org || 'demo')

  // State for form fields
  const [code, setCode] = useState('')
  const [nameEn, setNameEn] = useState('')
  const [nameFr, setNameFr] = useState('')
  const [descriptionEn, setDescriptionEn] = useState('')
  const [descriptionFr, setDescriptionFr] = useState('')

  // Sync form data when stakeholder type changes
  useMemo(() => {
    if (stakeholderType) {
      setCode(stakeholderType.code || '')

      if (typeof stakeholderType.names === 'object') {
        setNameEn(stakeholderType.names.en || '')
        setNameFr(stakeholderType.names.fr || '')
      } else {
        setNameEn(stakeholderType.names || '')
        setNameFr('')
      }

      if (stakeholderType.description) {
        if (typeof stakeholderType.description === 'object') {
          setDescriptionEn(stakeholderType.description.en || '')
          setDescriptionFr(stakeholderType.description.fr || '')
        } else {
          setDescriptionEn(stakeholderType.description || '')
          setDescriptionFr('')
        }
      } else {
        setDescriptionEn('')
        setDescriptionFr('')
      }
    }
  }, [stakeholderType])

  // Set breadcrumbs
  const breadcrumbs = useMemo(() => [
    { title: t('breadcrumb.home'), path: `/${org}` },
    { title: t('breadcrumb.org_settings'), path: `/${org}/settings` },
    { title: t('stakeholder_types.title'), path: `/${org}/contract-stakeholder-types` },
    { title: stakeholderType ? (typeof stakeholderType.names === 'object' ? stakeholderType.names.en || stakeholderType.names.fr || t('stakeholder_types.edit_title') : stakeholderType.names || t('stakeholder_types.edit_title')) : t('stakeholder_types.edit_title') }
  ], [org, stakeholderType, t])

  useBreadcrumbs(breadcrumbs)

  const handleSave = async () => {
    if (!stakeholderType) return

    const names: Record<string, string> = {}
    if (nameEn) names.en = nameEn
    if (nameFr) names.fr = nameFr

    const description: Record<string, string> = {}
    if (descriptionEn) description.en = descriptionEn
    if (descriptionFr) description.fr = descriptionFr

    try {
      await updateMutation.mutateAsync({
        code: code.trim() || stakeholderType.code,
        names: Object.keys(names).length > 0 ? names : stakeholderType.names,
        description: Object.keys(description).length > 0 ? description : stakeholderType.description
      })
    } catch {
      toast({
        title: t('common:status.error'),
        description: t('stakeholder_types.error_update'),
        variant: "destructive"
      })
    }
  }

  const handleDelete = async () => {
    if (!stakeholderType || !confirm(t('stakeholder_types.delete_confirm'))) {
      return
    }

    try {
      await deleteMutation.mutateAsync(stakeholderType.uid)

      toast({
        title: t('stakeholder_types.deleted_title'),
        description: t('stakeholder_types.deleted_description')
      })

      navigate({ to: '/$org/contract-stakeholder-types', params: { org: org || 'demo' } })
    } catch {
      toast({
        title: t('common:status.error'),
        description: t('stakeholder_types.error_delete'),
        variant: "destructive"
      })
    }
  }

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 max-w-4xl">
        <div className="flex items-center justify-center h-64">
          <div className="motion-safe:animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    )
  }

  if (error || !stakeholderType) {
    return (
      <div className="container mx-auto py-8 max-w-4xl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-2">Error</h1>
          <p className="text-muted-foreground">
            {(error as any)?.title || (error as any)?.message || t('stakeholder_types.not_found')}
          </p>
          <Link to="/$org/contract-stakeholder-types" params={{ org: org || 'demo' }}>
            <Button className="mt-4">
              <AppIcon path={mdiArrowLeft} size="sm" className="mr-2" />
              {t('stakeholder_types.back_to_list')}
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t('stakeholder_types.edit_title')}</h1>
            </div>
          </div>
          {canWrite && (
            <div className="flex items-center space-x-2">
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                data-testid="delete-stakeholder-type"
              >
                <AppIcon path={mdiTrashCanOutline} size="sm" className="mr-2" />
                {t('common:actions.delete')}
              </Button>
              <Button
                onClick={handleSave}
                disabled={updateMutation.isPending}
                data-testid="save-stakeholder-type"
              >
                <AppIcon path={mdiContentSave} size="sm" className="mr-2" />
                {t('common:actions.save')}
              </Button>
            </div>
          )}
        </div>

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle>{t('stakeholder_types.form_title')}</CardTitle>
            <CardDescription>
              {t('stakeholder_types.form_description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Code */}
            <div className="space-y-2">
              <Label htmlFor="code">{t('stakeholder_types.field_code')} *</Label>
              <Input
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
                placeholder={t('stakeholder_types.placeholder_code')}
                readOnly={!canWrite}
                data-testid="code-input"
              />
              <p className="text-xs text-muted-foreground">
                {t('stakeholder_types.code_hint')}
              </p>
            </div>

            {/* English Name */}
            <div className="space-y-2">
              <Label htmlFor="name-en">{t('stakeholder_types.field_name_en')} *</Label>
              <Input
                id="name-en"
                value={nameEn}
                onChange={(e) => setNameEn(e.target.value)}
                placeholder={t('stakeholder_types.placeholder_name_en')}
                readOnly={!canWrite}
                data-testid="name-en-input"
              />
            </div>

            {/* French Name */}
            <div className="space-y-2">
              <Label htmlFor="name-fr">{t('stakeholder_types.field_name_fr')}</Label>
              <Input
                id="name-fr"
                value={nameFr}
                onChange={(e) => setNameFr(e.target.value)}
                placeholder={t('stakeholder_types.placeholder_name_fr')}
                readOnly={!canWrite}
                data-testid="name-fr-input"
              />
            </div>

            {/* English Description */}
            <div className="space-y-2">
              <Label htmlFor="description-en">{t('stakeholder_types.field_description_en')}</Label>
              <Textarea
                id="description-en"
                value={descriptionEn}
                onChange={(e) => setDescriptionEn(e.target.value)}
                placeholder={t('stakeholder_types.placeholder_description_en')}
                readOnly={!canWrite}
                rows={3}
                data-testid="description-en-input"
              />
            </div>

            {/* French Description */}
            <div className="space-y-2">
              <Label htmlFor="description-fr">{t('stakeholder_types.field_description_fr')}</Label>
              <Textarea
                id="description-fr"
                value={descriptionFr}
                onChange={(e) => setDescriptionFr(e.target.value)}
                placeholder={t('stakeholder_types.placeholder_description_fr')}
                readOnly={!canWrite}
                rows={3}
                data-testid="description-fr-input"
              />
            </div>

            {/* Metadata */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <Label className="text-sm text-muted-foreground">{t('stakeholder_types.field_created')}</Label>
                <p className="text-sm">
                  {stakeholderType.created_at ? new Date(stakeholderType.created_at).toLocaleString() : '-'}
                </p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">{t('stakeholder_types.field_updated')}</Label>
                <p className="text-sm">
                  {stakeholderType.updated_at ? new Date(stakeholderType.updated_at).toLocaleString() : '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}